import { enablePointerEvents } from '@utils/pointer-events-control';
import { config } from '@js/index';

/**
 * Level 1 — Combined Fruit Harvest.
 * Direct port of the legacy demo's GameScene + ShakeScene + MachineScene.
 * Three parallel scenes in the demo are consolidated into this single scene
 * (depth is preserved via creation order and explicit setDepth calls).
 */
export default class GameScene extends Phaser.Scene {
  constructor() {
    super({ key: 'GameScene' });
  }

  init() {
    this.gameVars = {
      entry: 0,
      round: 1,
      wrong: 0,
      correct: 0,
      firstInput: false,
      scrol: false,
      inputNumberUnit: undefined,
      inputNumberTenth: undefined,
      inputNumberHundredth: undefined,
    };
  }

  preload() {
    // L2 image assets are pre-loaded here so the scene transition into
    // LV2StartScene/LV2GameScene is seamless. (Mirrors demo behavior where
    // GameScene.preload() did the same.)
    this.load.image('lv2_startBG', 'assets/LV2assets/StartScene/lv2_startBG.png');
    this.load.image('lv2_truck', 'assets/LV2assets/StartScene/lv2_truck.png');
    this.load.image('lv2_truckMask', 'assets/LV2assets/StartScene/lv2_truckMask.png');
    this.load.image('wheels', 'assets/LV2assets/StartScene/wheels.png');

    this.load.atlas(
      'characterAnim',
      'assets/LV2assets/startAnim/characterAnim.png',
      'assets/LV2assets/startAnim/characterAnim.json',
    );
    this.load.atlas(
      'dustAnim',
      'assets/LV2assets/startAnim/dustAnim.png',
      'assets/LV2assets/startAnim/dustAnim.json',
    );
    this.load.atlas(
      'wheelCapAnim',
      'assets/LV2assets/startAnim/wheelCapAnim.png',
      'assets/LV2assets/startAnim/wheelCapAnim.json',
    );

    this.load.atlas(
      'boxAnim',
      'assets/LV2assets/anims/boxAnim.png',
      'assets/LV2assets/anims/boxAnim.json',
    );
    this.load.atlas(
      'conveyorAnim',
      'assets/LV2assets/anims/conveyorAnim.png',
      'assets/LV2assets/anims/conveyorAnim.json',
    );
    this.load.atlas(
      'orange01Anim',
      'assets/LV2assets/anims/orange01Anim.png',
      'assets/LV2assets/anims/orange01Anim.json',
    );
    this.load.atlas(
      'orange02Anim',
      'assets/LV2assets/anims/orange02Anim.png',
      'assets/LV2assets/anims/orange02Anim.json',
    );

    this.load.video(
      'endSceneVideo',
      'assets/LV2assets/endSceneVideo.mp4',
      'loadeddata',
      false,
      true,
    );

    this.load.atlas(
      'confettiAnim',
      'assets/GameScene/confettiAnim.png',
      'assets/GameScene/confettiAnim.json',
    );
    this.load.image(
      'lv2_gameSceneBGTop',
      'assets/LV2assets/GameScene/lv2_gameSceneBGTop.png',
    );
    this.load.image('lv2_gameSceneBG', 'assets/LV2assets/GameScene/lv2_gameSceneBG.png');
    this.load.image('lv2_grass', 'assets/LV2assets/GameScene/lv2_grass.png');
    this.load.image('lv2_leftTruck', 'assets/LV2assets/GameScene/lv2_leftTruck.png');
    this.load.image(
      'lv2_leftTruckCover',
      'assets/LV2assets/GameScene/lv2_leftTruckCover.png',
    );
    this.load.image('lv2_machine', 'assets/LV2assets/GameScene/lv2_machine.png');
    this.load.image('lv2_orange', 'assets/LV2assets/GameScene/lv2_orange.png');
    this.load.image('lv2_pack', 'assets/LV2assets/GameScene/lv2_pack.png');
    this.load.image('lv2_packHover', 'assets/LV2assets/GameScene/lv2_packHover.png');
    this.load.image('lv2_qn1', 'assets/LV2assets/GameScene/lv2_qn1.png');
    this.load.image('lv2_qn2', 'assets/LV2assets/GameScene/lv2_qn2.png');
    this.load.image('lv2_qn3', 'assets/LV2assets/GameScene/lv2_qn3.png');
    this.load.image('lv2_rightTruck', 'assets/LV2assets/GameScene/lv2_rightTruck.png');
    this.load.image(
      'lv2_rightTruckCover',
      'assets/LV2assets/GameScene/lv2_rightTruckCover.png',
    );
    this.load.image('lv2_singleWheel', 'assets/LV2assets/GameScene/lv2_singleWheel.png');
    this.load.image('lv2_unitBox', 'assets/LV2assets/GameScene/lv2_unitBox.png');
    this.load.image('lv2_mask', 'assets/LV2assets/GameScene/lv2_mask.png');
  }

  create() {
    // Enable pointer events so hover states and the cursor work
    enablePointerEvents();

    this.cam = this.cameras.main;
    this.cam.fadeIn(100, 255, 255, 255);

    // ---- Background + trees (was GameScene) ----
    this.BG = this.add
      .image(-2200, this.cameras.main.height / 2 - 160, 'BG2')
      .setScale(1.0);

    // Tree shake animations
    this.anims.create({
      key: 'shake1',
      frames: this.anims.generateFrameNames('shake', {
        start: 0,
        end: 60,
        zeroPad: 0,
        prefix: 'treeShake',
        suffix: '.png',
      }),
      frameRate: 28,
      repeat: -1,
    });
    this.anims.create({
      key: 'shake2',
      frames: this.anims.generateFrameNames('shake', {
        start: 60,
        end: 0,
        zeroPad: 0,
        prefix: 'treeShake',
        suffix: '.png',
      }),
      frameRate: 15,
      repeat: -1,
    });
    this.anims.create({
      key: 'shake3',
      frames: this.anims.generateFrameNames('shake', {
        start: 60,
        end: 180,
        zeroPad: 0,
        prefix: 'treeShake',
        suffix: '.png',
      }),
      frameRate: 30,
      repeat: 0,
    });

    this.tree1 = this.add.sprite(233, 315, 'shake').play('shake1');
    this.tree1.displayHeight = 590;
    this.tree2 = this.add.sprite(438, 355, 'shake').play('shake2');
    this.tree3 = this.add.sprite(650, 315, 'shake').play('shake2');
    this.tree3.displayHeight = 590;
    this.tree4 = this.add.sprite(855, 245, 'shake').setScale(1.09).play('shake1');
    this.tree4.displayHeight = 640;
    this.tree5 = this.add.sprite(1080, 320, 'shake').play('shake2');
    this.bottom = this.add.image(-500, 575, 'bottom').setScale(0.8);

    // ---- Basket (was ShakeScene) ----
    this.cam2 = this.cameras.main; // demo's cam2 used for shake effects
    this.basket = this.add.image(644, 660, 'basket').setScale(1.02);
    this.leftHandle = this.add.image(250, 750, 'leftHandle');
    this.leftHandle.angle -= 20;
    this.rightHandle = this.add.image(1035, 750, 'rightHandle');
    this.rightHandle.angle += 20;
    this.containerBasket = this.add.container(0, 0, [
      this.basket,
      this.leftHandle,
      this.rightHandle,
    ]);

    // Audio (was ShakeScene)
    config.audio.CorrectAnswer = this.sound.add('CorrectAnswer').setVolume(0.1);
    config.audio.HarvesterArmLift = this.sound.add('HarvesterArmLift').setVolume(0.1);
    config.audio.harvesterRunning = this.sound.add('harvesterRunning').setVolume(0.08);
    config.audio.harvesterRunning.play();
    config.audio.IncorrectAnswer = this.sound.add('IncorrectAnswer').setVolume(0.1);
    config.audio.treeShakingGameScene = this.sound
      .add('treeShakingGameScene')
      .setVolume(0.1);
    config.audio.WrongAnswerharvesterShake = this.sound
      .add('WrongAnswerharvesterShake')
      .setVolume(0.1);
    config.audio.RewardRevealL1 = this.sound.add('RewardRevealL1').setVolume(0.1);

    // ---- Machine (was MachineScene) ----
    this.cam3 = this.cameras.main; // demo's cam3 used for machine shake

    // Wind animation
    this.anims.create({
      key: 'wind',
      frames: this.anims.generateFrameNames('wind', {
        start: 0,
        end: 210,
        zeroPad: 0,
        prefix: 'windLeaves',
        suffix: '.png',
      }),
      frameRate: 20,
      repeat: -1,
    });
    this.wind = this.add.sprite(640, 360, 'wind').play('wind');

    // Confetti animation
    this.anims.create({
      key: 'confetti',
      frames: this.anims.generateFrameNames('confetti', {
        start: 0,
        end: 81,
        zeroPad: 0,
        prefix: 'confetti',
        suffix: '.png',
      }),
      frameRate: 35,
      repeat: 0,
    });

    // Lever animations
    this.anims.create({
      key: 'lever',
      frames: this.anims.generateFrameNames('lever', {
        start: 0,
        end: 16,
        zeroPad: 0,
        prefix: 'lever',
        suffix: '.png',
      }),
      frameRate: 35,
      repeat: 0,
    });
    this.anims.create({
      key: 'leverUp',
      frames: this.anims.generateFrameNames('lever', {
        start: 16,
        end: 0,
        zeroPad: 0,
        prefix: 'lever',
        suffix: '.png',
      }),
      frameRate: 20,
      repeat: 0,
    });

    // Spotlight
    this.anims.create({
      key: 'spotlightOpen',
      frames: this.anims.generateFrameNames('spotlight', {
        start: 0,
        end: 188,
        zeroPad: 0,
        prefix: 'spotlight0',
        suffix: '.png',
      }),
      frameRate: 20,
      repeat: 0,
    });

    // End confetti
    this.anims.create({
      key: 'endConfetti',
      frames: this.anims.generateFrameNames('endConfetti', {
        start: 0,
        end: 120,
        zeroPad: 0,
        prefix: 'seq_0_',
        suffix: '.png',
      }),
      frameRate: 40,
      repeat: 0,
    });

    // Hidden duplicate basket (the demo's MachineScene also created a
    // ghost basket/handles that stayed invisible but were part of containerMachine).
    // Keeping behavior identical to demo.
    this.basketGhost = this.add
      .image(644, 660, 'basket')
      .setScale(1.02)
      .setVisible(false);
    this.leftHandleGhost = this.add.image(250, 750, 'leftHandle').setVisible(false);
    this.leftHandleGhost.angle -= 20;
    this.rightHandleGhost = this.add.image(1035, 750, 'rightHandle').setVisible(false);
    this.rightHandleGhost.angle += 20;

    this.machine = this.add.image(645, 542, 'machine');
    this.harvest = this.add
      .image(645, 900, 'harvest')
      .setInteractive({ cursor: 'pointer' });
    this.knob = this.add.image(984, 630, 'knob');
    this.wheelPart = this.add.image(360, 603, 'wheelPart');
    this.wheel = this.add.image(350, 590, 'wheel');
    this.lever = this.add.sprite(645, 355, 'lever');

    this.bulb = this.add.image(477, 435, 'bulb').setVisible(false);
    this.bulb2 = this.add.image(477, 476, 'bulb').setVisible(false);
    this.bulb3 = this.add.image(477, 519, 'bulb').setVisible(false);
    this.redBulbLeft = this.add.image(509, 643, 'redBulb').setVisible(false);
    this.redBulbRight = this.add.image(780, 643, 'redBulb').setVisible(false);

    this.inputBox1 = this.add
      .image(646, 532, 'default')
      .setInteractive({ cursor: 'pointer' });
    this.inputBox2 = this.add
      .image(708, 532, 'default')
      .setInteractive({ cursor: 'pointer' });
    this.inputBox3 = this.add
      .image(583, 532, 'default')
      .setInteractive({ cursor: 'pointer' });

    const fontStyleBig = { fontFamily: 'font2', fontSize: '50px', fill: '#2B2B2B' };
    const fontStyleInput = { fontFamily: 'font2', fontSize: '45px', fill: '#2B2B2B' };

    this.multiplicant = this.add.text(639, 385, '1   4', fontStyleBig).setResolution(1.0);
    this.multiplier = this.add.text(693, 445, '5', fontStyleBig).setResolution(1.0);
    this.multiply = this.add.text(635, 445, 'x', fontStyleBig).setResolution(1.0);
    this.inputTextUnit = this.add.text(699, 507, '', fontStyleInput).setResolution(1.0);
    this.inputTextTenth = this.add.text(625, 507, '', fontStyleInput).setResolution(1.0);
    this.inputTextHundredth = this.add
      .text(550, 507, '', fontStyleInput)
      .setResolution(1.0);

    this.cursorHundredth = this.add
      .image(583, 516, 'cursor')
      .setOrigin(0)
      .setVisible(false);
    this.cursorTenth = this.add.image(647, 516, 'cursor').setOrigin(0).setVisible(false);
    this.cursorUnit = this.add.image(709, 516, 'cursor').setOrigin(0).setVisible(false);

    this.containerMachine = this.add
      .container(0, 0, [
        this.basketGhost,
        this.leftHandleGhost,
        this.rightHandleGhost,
        this.machine,
        this.harvest,
        this.knob,
        this.wheelPart,
        this.wheel,
        this.lever,
        this.bulb,
        this.bulb2,
        this.bulb3,
        this.redBulbLeft,
        this.redBulbRight,
        this.inputBox1,
        this.inputBox2,
        this.inputBox3,
        this.multiplicant,
        this.multiplier,
        this.multiply,
        this.inputTextUnit,
        this.inputTextTenth,
        this.inputTextHundredth,
        this.cursorHundredth,
        this.cursorTenth,
        this.cursorUnit,
      ])
      .setDepth(1);
    this.wind.setDepth(2);

    this.bindInputBoxHandlers();
    this.bindKeyboard();
    this.bindHarvestButton();
  }

  // ==================== INPUT WIRING ====================

  bindInputBoxHandlers() {
    this.inputBox1.on('pointerup', () => {
      this.input.keyboard.enabled = true;
      this.gameVars.entry = 2;
      this.gameVars.wrong = 0;
      this.flash1 = this.plugins
        .get('rexflashplugin')
        .add(this.cursorTenth, { duration: 700, repeat: 1000 }, this);
      this.inputBox1.setTexture('typing');
      this.inputBox2.setTexture('default');
      this.inputBox3.setTexture('default');
      this.cursorTenth.setVisible(true);
      this.flash1.flash();
    });

    this.inputBox2.on('pointerup', () => {
      this.input.keyboard.enabled = true;
      this.gameVars.entry = 1;
      this.gameVars.wrong = 0;
      this.flash2 = this.plugins
        .get('rexflashplugin')
        .add(this.cursorUnit, { duration: 700, repeat: 1000 }, this);
      this.inputBox2.setTexture('typing');
      this.inputBox1.setTexture('default');
      this.inputBox3.setTexture('default');
      this.cursorUnit.setVisible(true);
      this.flash2.flash();
    });

    this.inputBox3.on('pointerup', () => {
      this.input.keyboard.enabled = true;
      this.gameVars.entry = 3;
      this.gameVars.wrong = 0;
      this.flash3 = this.plugins
        .get('rexflashplugin')
        .add(this.cursorHundredth, { duration: 700, repeat: 1000 }, this);
      this.inputBox3.setTexture('typing');
      this.inputBox1.setTexture('default');
      this.inputBox2.setTexture('default');
      this.cursorHundredth.setVisible(true);
      this.flash3.flash();
    });
  }

  bindKeyboard() {
    this.input.keyboard.on(
      'keydown',
      event => {
        this.inputTextUnit.setFill('#2B2B2B');
        this.inputTextTenth.setFill('#2B2B2B');
        this.inputTextHundredth.setFill('#2B2B2B');
        this.gameVars.firstInput = true;
        if (this.gameVars.firstInput === true) this.harvestSlideUp();

        if (this.gameVars.entry === 1) {
          this.gameVars.inputNumberUnit = parseInt(event.key, 10);
          this.inputTextUnit.x = this.gameVars.inputNumberUnit === 1 ? 701 : 695;
          this.inputTextUnit.setText(this.gameVars.inputNumberUnit);
        }

        if (this.gameVars.entry === 2) {
          this.gameVars.inputNumberTenth = parseInt(event.key, 10);
          this.inputTextTenth.x = this.gameVars.inputNumberTenth === 1 ? 637 : 633;
          this.inputTextTenth.setText(this.gameVars.inputNumberTenth);
        }

        if (this.gameVars.entry === 3) {
          this.gameVars.inputNumberHundredth = parseInt(event.key, 10);
          this.inputTextHundredth.x =
            this.gameVars.inputNumberHundredth === 1 ? 574 : 570;
          this.inputTextHundredth.setText(this.gameVars.inputNumberHundredth);
        }
      },
      this,
    );
  }

  bindHarvestButton() {
    this.harvest.on('pointerover', () => {
      this.harvest.setTexture('harvestBlack');
    });

    this.harvest.on('pointerout', () => {
      this.harvest.setTexture('harvest');
    });

    this.harvest.on('pointerdown', () => {
      this.tweens.add({
        targets: this.harvest,
        scale: 0.8,
        duration: 100,
        ease: 'Power2',
        repeat: 0,
        yoyo: true,
      });

      const u = this.inputTextUnit.text;
      const t = this.inputTextTenth.text;
      const h = this.inputTextHundredth.text;
      if (
        (u.length > 0 && t.length > 0) ||
        (u.length > 0 && h.length > 0) ||
        (t.length > 0 && h.length > 0) ||
        u.length > 0 ||
        t.length > 0 ||
        h.length > 0
      ) {
        const r = this.gameVars.round;
        if (r === 1) {
          if (u === '0' && t === '7' && (h === '' || h === '0'))
            this.correctAnswerCheck();
          else this.wrongAnswerCheck();
        }
        if (r === 2) {
          if (u === '0' && t === '0' && h === '1') this.correctAnswerCheck();
          else this.wrongAnswerCheck();
        }
        if (r === 3) {
          if (u === '0' && t === '8' && h === '7') this.correctAnswerCheck();
          else this.wrongAnswerCheck();
        }
      }
    });
  }

  // ==================== ANSWER FLOW ====================

  harvestSlideUp() {
    this.tweens.add({
      targets: this.harvest,
      y: 650,
      duration: 500,
      ease: 'Linear',
      repeat: 0,
      yoyo: false,
    });
    this.gameVars.firstInput = false;
  }

  correctAnswerCheck() {
    this.gameVars.correct = 1;
    this.containerBasket.setVisible(true);
    this.basketGhost.setVisible(false);
    this.leftHandleGhost.setVisible(false);
    this.rightHandleGhost.setVisible(false);

    this.confetti = this.add.sprite(650, 255, 'confetti').setDepth(1).play('confetti');
    if (this.gameVars.round === 1) {
      this.confetti.setDepth(2);
      this.containerMachine.setDepth(1);
    }
    if (this.gameVars.round >= 2) this.confetti.setDepth(4);

    this.inputBox1.setTexture('correct');
    this.inputBox2.setTexture('correct');
    if (this.gameVars.round === 1) {
      if (this.inputTextHundredth.text === '0') this.inputBox3.setTexture('correct');
      if (this.inputTextHundredth.text === '') this.inputBox3.setTexture('default');
    }
    if (this.gameVars.round > 1) this.inputBox3.setTexture('correct');

    this.inputTextUnit.setFill('#149A53');
    this.inputTextTenth.setFill('#149A53');
    this.inputTextHundredth.setFill('#149A53');

    this.input.keyboard.enabled = false;
    this.inputBox1.disableInteractive();
    this.inputBox2.disableInteractive();
    this.inputBox3.disableInteractive();
    this.cursorUnit.setVisible(false);
    this.cursorTenth.setVisible(false);
    this.cursorHundredth.setVisible(false);
    this.harvest.setTexture('harvest');
    this.harvest.disableInteractive();
    this.celebrate();
  }

  celebrate() {
    config.audio.CorrectAnswer.play();

    this.time.delayedCall(
      1000,
      () => {
        config.audio.HarvesterArmLift.play();

        this.tweens.add({
          targets: this.harvest,
          y: 900,
          duration: 1000,
          ease: 'Linear',
          repeat: 0,
          yoyo: false,
        });

        this.lever.anims.play('lever', true);

        this.knobTween = this.tweens.add({
          targets: this.knob,
          x: { from: 984, to: 995 },
          y: 670,
          duration: 500,
          ease: 'Linear',
          repeat: 0,
          yoyo: false,
        });

        this.basketUpTween = this.tweens.add({
          targets: this.containerBasket,
          y: -180,
          duration: 700,
          ease: 'Linear',
          repeat: 0,
          yoyo: false,
        });

        this.basketUpTween.on('complete', () => {
          if (this.gameVars.round === 1) this.bulb.setVisible(true);
          if (this.gameVars.round === 2) this.bulb2.setVisible(true);
          if (this.gameVars.round === 3) this.bulb3.setVisible(true);

          this.cam2.shake(500, 0.005);

          if (this.gameVars.round === 1 || this.gameVars.round === 2) {
            this.basketShakeTween = this.tweens.add({
              targets: this.containerBasket,
              x: { from: -40, to: 40 },
              duration: 100,
              ease: 'Linear',
              repeat: 6,
              yoyo: true,
            });
          }

          if (this.gameVars.round === 3) {
            this.basketShakeTween = this.tweens.add({
              targets: this.containerBasket,
              x: { from: -55, to: 55 },
              duration: 160,
              ease: 'Linear',
              repeat: 6,
              yoyo: true,
            });
          }

          this.tree1.anims.play('shake3');
          this.tree2.anims.play('shake3');
          this.tree3.anims.play('shake3');
          this.tree4.anims.play('shake3');

          config.audio.treeShakingGameScene.play();

          this.time.delayedCall(2800, () => {
            config.audio.treeShakingGameScene.stop();
          });

          if (this.gameVars.round === 1 || this.gameVars.round === 3)
            this.tree5.anims.play('shake3');
          if (this.gameVars.round === 3 && this.tree12) this.tree12.anims.play('shake3');

          this.basketShakeTween.on('complete', () => {
            this.containerBasket.x = 0;
          });

          this.tree4.on(
            Phaser.Animations.Events.ANIMATION_COMPLETE_KEY + 'shake3',
            () => {
              this.afterCollectReset();
            },
          );
        });
      },
      [],
      this,
    );
  }

  afterCollectReset() {
    config.audio.HarvesterArmLift.play();

    this.basketDownTween = this.tweens.add({
      targets: this.containerBasket,
      y: 0,
      duration: 700,
      ease: 'Linear',
      repeat: 0,
      yoyo: false,
    });

    this.lever.anims.play('leverUp', true);

    this.knobUpTween = this.tweens.add({
      targets: this.knob,
      x: { from: 995, to: 984 },
      y: 630,
      duration: 1000,
      ease: 'Linear',
      repeat: 0,
      yoyo: false,
    });

    this.knobUpTween.on('complete', () => {
      this.scrollNew();
    });
  }

  scrollNew() {
    this.gameVars.round += 1;

    this.inputBox1.setTexture('default');
    this.inputBox2.setTexture('default');
    this.inputBox3.setTexture('default');

    if (this.gameVars.round === 2) {
      this.tree6 = this.add.sprite(1745, 255, 'shake').play('shake1');
      this.tree7 = this.add.sprite(1947, 320, 'shake').play('shake2');
      this.tree8 = this.add.sprite(2150, 280, 'shake').play('shake2');
      this.tree8.displayHeight = 570;
      this.tree9 = this.add.sprite(2360, 230, 'shake').setScale(1.03).play('shake1');
      this.tree9.displayHeight = 585;
    }

    if (this.gameVars.round === 3) {
      this.tree6 = this.add.sprite(1552, 288, 'shake').play('shake1');
      this.tree7 = this.add.sprite(1757, 325, 'shake').play('shake2');
      this.tree7.displayHeight = 500;
      this.tree8 = this.add.sprite(1962, 290, 'shake').play('shake2');
      this.tree9 = this.add.sprite(2160, 235, 'shake').setScale(1.09).play('shake1');
      this.tree9.displayHeight = 635;
      this.tree10 = this.add.sprite(2372, 350, 'shake').play('shake2');
      this.tree10.displayHeight = 525;
      this.tree11 = this.add.sprite(2570, 285, 'shake').setScale(1.05).play('shake1');
      this.tree11.displayHeight = 620;

      this.tree1.x = 345;
      this.tree2.x = 547;
      this.tree3.x = 750;
      this.tree4.x = 960;
      this.BG.x = -3595;
      this.bottom.x = -1900;

      this.scrollContainer = this.add.container(0, 0, [
        this.BG,
        this.tree1,
        this.tree2,
        this.tree3,
        this.tree4,
        this.tree6,
        this.tree7,
        this.tree8,
        this.tree9,
        this.tree10,
        this.tree11,
        this.bottom,
      ]);

      this.scrollTween = this.tweens.add({
        targets: this.scrollContainer,
        x: -1400,
        duration: 1500,
        ease: 'Linear',
        repeat: 0,
        yoyo: false,
      });
    }

    this.containerMachineWrap = this.add.container(0, 0, [
      this.containerBasket,
      this.basketGhost,
      this.leftHandleGhost,
      this.rightHandleGhost,
      this.machine,
      this.redBulbLeft,
      this.redBulbRight,
      this.bulb,
      this.bulb2,
      this.bulb3,
      this.inputBox1,
      this.inputBox2,
      this.inputBox3,
      this.multiplicant,
      this.multiplier,
      this.multiply,
      this.inputTextUnit,
      this.inputTextTenth,
      this.inputTextHundredth,
      this.cursorHundredth,
      this.cursorTenth,
      this.cursorUnit,
      this.wheelPart,
      this.wheel,
      this.lever,
      this.knob,
      this.harvest,
    ]);

    if (this.gameVars.round > 1) {
      this.containerMachineWrap.setDepth(4);
      this.containerBasket.setVisible(true);
      if (this.gameVars.round === 2) this.gameVars.scrol = true;
    }
    this.wind.setDepth(5);

    if (this.gameVars.round <= 3) {
      this.tweens.add({
        targets: this.wheel,
        angle: 240,
        duration: 1250,
        ease: 'Linear',
        repeat: 0,
        yoyo: false,
      });
    }

    this.blurTween = this.tweens.add({
      targets: [
        this.multiplicant,
        this.multiplier,
        this.multiply,
        this.inputTextUnit,
        this.inputTextTenth,
        this.inputTextHundredth,
      ],
      alpha: 0,
      duration: 1000,
      ease: 'Linear',
      repeat: 0,
      yoyo: false,
    });

    this.blurTween.on('complete', () => {
      if (this.gameVars.round === 2) {
        this.multiplicant.x = 631;
        this.multiplier.x = 693;
        this.multiplicant.text = '2   5';
        this.multiplier.text = '4';
      }
      if (this.gameVars.round === 3) {
        this.multiplicant.x = 566;
        this.multiplicant.text = '1    3   0';
        this.multiplier.text = '6';
        this.multiplier.x = 696;
      }
      this.inputTextHundredth.text = '';
      this.inputTextTenth.text = '';
      this.inputTextUnit.text = '';
      this.gameVars.entry = 0;
      this.gameVars.wrong = 1;
      this.gameVars.correct = 0;
      this.gameVars.firstInput = false;

      this.time.delayedCall(350, () => {
        if (this.gameVars.round <= 3) {
          this.tweens.add({
            targets: [
              this.multiplicant,
              this.multiplier,
              this.multiply,
              this.inputTextHundredth,
              this.inputTextTenth,
              this.inputTextUnit,
            ],
            alpha: 1,
            duration: 1000,
            ease: 'Linear',
            repeat: 0,
            yoyo: false,
          });
        }

        this.inputBox1.setInteractive();
        this.inputBox2.setInteractive();
        this.inputBox3.setInteractive();
        this.harvest.setInteractive();

        this.tree1 = this.tree6;
        this.tree2 = this.tree7;
        this.tree3 = this.tree8;
        this.tree4 = this.tree9;
        if (this.gameVars.round === 3) {
          this.tree5 = this.tree10;
          this.tree12 = this.tree11;

          this.time.delayedCall(1500, () => {
            this.gameVars.scrol = true;
          });
        }

        if (this.gameVars.round === 4) this.endCelebration();
      });
    });
  }

  wrongAnswerCheck() {
    config.audio.WrongAnswerharvesterShake.play();

    this.containerBasket.setVisible(false);
    this.basketGhost.setVisible(true);
    this.leftHandleGhost.setVisible(true);
    this.rightHandleGhost.setVisible(true);

    this.cam3.shake(1300, 0.009);

    if (this.inputTextTenth.text.length > 0) this.inputBox1.setTexture('wrong');
    if (this.inputTextHundredth.text.length > 0) this.inputBox3.setTexture('wrong');
    if (this.inputTextUnit.text.length > 0) this.inputBox2.setTexture('wrong');
    if (this.inputTextTenth.text.length <= 0) this.inputBox1.setTexture('default');
    if (this.inputTextHundredth.text.length <= 0) this.inputBox3.setTexture('default');
    if (this.inputTextUnit.text.length <= 0) this.inputBox2.setTexture('default');

    this.inputTextHundredth.setFill('#C22300');
    this.inputTextUnit.setFill('#C22300');
    this.inputTextTenth.setFill('#C22300');
    this.redBulbLeft.setVisible(true);
    this.redBulbRight.setVisible(true);
    this.bulbBlinking();

    this.input.keyboard.enabled = false;
    this.inputBox1.disableInteractive();
    this.inputBox2.disableInteractive();
    this.inputBox3.disableInteractive();
    this.harvest.setTexture('harvest');
    this.harvest.disableInteractive();
    this.gameVars.wrong = 1;

    this.time.delayedCall(1300, () => {
      this.inputBox1.setTexture('default');
      this.inputBox2.setTexture('default');
      this.inputBox3.setTexture('default');
      this.inputTextUnit.text = '';
      this.inputTextTenth.text = '';
      this.inputTextHundredth.text = '';
      this.inputBox1.setInteractive();
      this.inputBox2.setInteractive();
      this.inputBox3.setInteractive();
      this.cursorUnit.setVisible(false);
      this.cursorTenth.setVisible(false);
      this.cursorHundredth.setVisible(false);
      this.redBulbLeft.setVisible(false);
      this.redBulbRight.setVisible(false);
      this.harvest.setInteractive();
    });
  }

  bulbBlinking() {
    this.tweens.add({
      targets: [this.redBulbLeft, this.redBulbRight],
      delay: 15,
      alpha: { from: 0, to: 1 },
      ease: 'Cubic',
      duration: 250,
      repeat: -1,
      yoyo: true,
    });
  }

  endCelebration() {
    this.overlay = this.add
      .image(this.cameras.main.width / 2, this.cameras.main.height / 2, 'overlay')
      .setDepth(6);
    this.endConfetti = this.add
      .sprite(this.cameras.main.width / 2, this.cameras.main.height / 2, 'endConfetti')
      .setDepth(7)
      .play('endConfetti');

    this.endConfetti.on(
      Phaser.Animations.Events.ANIMATION_COMPLETE_KEY + 'endConfetti',
      () => {
        this.spotlight = this.add
          .sprite(this.cameras.main.width / 2, this.cameras.main.height / 2, 'spotlight')
          .setDepth(8)
          .play('spotlightOpen');
        this.yay = this.add
          .sprite(this.cameras.main.width / 2, this.cameras.main.height / 2 + 100, 'yay')
          .setDepth(9)
          .setAlpha(0);

        this.tweens.add({
          targets: this.yay,
          alpha: 1.0,
          delay: 250,
          duration: 1000,
        });

        this.time.delayedCall(1000, () => {
          this.bunchApples = this.add
            .sprite(
              this.cameras.main.width / 2,
              this.cameras.main.height / 2 - 70,
              'bunchApples',
            )
            .setDepth(9)
            .setAlpha(0);

          this.tweens.add({
            targets: this.bunchApples,
            alpha: 1.0,
            delay: 250,
            duration: 1000,
          });

          config.audio.RewardRevealL1.play();
          config.audio.harvesterRunning.stop();

          this.distribute = this.add
            .sprite(
              this.cameras.main.width / 2,
              this.cameras.main.height / 2 + 200,
              'distribute',
            )
            .setDepth(9)
            .setAlpha(0)
            .setInteractive({ cursor: 'pointer' });

          this.tweens.add({
            targets: this.distribute,
            alpha: 1.0,
            delay: 250,
            duration: 1000,
          });

          this.distribute.on('pointerover', () => {
            this.distribute.setTexture('distributeBlack');
          });
          this.distribute.on('pointerout', () => {
            this.distribute.setTexture('distribute');
          });
          this.distribute.on('pointerdown', () => {
            this.time.delayedCall(10, () => {
              this.cameras.main.fadeOut(1000, 255, 255, 255);
            });
            this.cameras.main.once('camerafadeoutcomplete', () => {
              this.scene.stop('GameScene');
              this.scene.start('LV2StartScene');
            });
          });
        });
      },
    );
  }

  // ==================== UPDATE ====================

  update() {
    // Background scrolling logic — replicates demo's GameScene.update()
    if (this.gameVars.round === 2 && this.gameVars.scrol === true) {
      this.scrollContainer = this.add.container(0, 0, [
        this.BG,
        this.tree1,
        this.tree2,
        this.tree3,
        this.tree4,
        this.tree5,
        this.tree6,
        this.tree7,
        this.tree8,
        this.tree9,
        this.bottom,
      ]);
      this.scrollTween = this.tweens.add({
        targets: this.scrollContainer,
        x: -1400,
        duration: 1500,
        ease: 'Linear',
        repeat: 0,
        yoyo: false,
      });
      this.gameVars.scrol = false;
    }

    if (this.gameVars.round === 3 && this.gameVars.scrol === true) {
      this.scrollContainer3 = this.add.container(0, 0, [
        this.BG,
        this.tree1,
        this.tree2,
        this.tree3,
        this.tree4,
        this.tree5,
        this.tree12,
        this.bottom,
      ]);
      if (this.scrollContainer) this.scrollContainer.setVisible(false);
      this.BG.x -= 1400;
      this.tree1.x -= 1400;
      this.tree2.x -= 1400;
      this.tree3.x -= 1400;
      this.tree4.x -= 1400;
      this.tree5.x -= 1400;
      if (this.tree12) this.tree12.x -= 1400;
      this.bottom.x -= 1400;
      this.gameVars.scrol = false;
    }

    // Cursor visibility (was MachineScene.update)
    if (this.gameVars.wrong === 0) {
      if (
        this.inputTextUnit.text.length > 0 ||
        this.gameVars.entry === 2 ||
        this.gameVars.entry === 3
      )
        this.cursorUnit.setVisible(false);
      if (
        this.inputTextTenth.text.length > 0 ||
        this.gameVars.entry === 1 ||
        this.gameVars.entry === 3
      )
        this.cursorTenth.setVisible(false);
      if (
        this.inputTextHundredth.text.length > 0 ||
        this.gameVars.entry === 2 ||
        this.gameVars.entry === 1
      )
        this.cursorHundredth.setVisible(false);
    }
    if (this.gameVars.wrong === 1 || this.gameVars.correct === 1) {
      this.cursorUnit.setVisible(false);
      this.cursorTenth.setVisible(false);
      this.cursorHundredth.setVisible(false);
    }
  }
}
